import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DriverScore } from '../../../backendsdk';
import { TrackedLink as Link } from '../../../components/TrackedComponents';
import useApi from '../../../hooks/api';
import useCallbackRef from '../../../hooks/callbackRef';
import { getDriverDisplayName } from '../../../hooks/drivers';
import useProfile from '../../../hooks/profile';
import { driversPageDefs } from '../../../utils/Pages';
import { DEFAULT_TITLE } from '../../Layout';
import { COMBINED_EVENT_TYPE_MAP } from '../Event/Defs';
import { INVALID_SCORE } from '../Metrics/SafetyWellness/scoreConstants';
import { CARD_HEIGHT, CARD_MARGIN, CARD_WIDTH } from './DriverCard';
import DriverGrid from './DriverGrid';
import { NAV_BUTTON_WIDTH } from './DriverGrid';
import DriversToolbarComponent from './DriversToolbarComponent';

const DriversComponent: React.FC = () => {
    const [driverScores, setDriverScores] = useState<DriverScore[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isError, setIsError] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>('');
    const [subfleetValue, setSubfleetValue] = useState<string[]>([]);
    const [behaviorValue, setBehaviorValue] = useState<string[]>([]);
    const [includeAllDrivers, setIncludeAllDrivers] = useState<boolean>(false);
    const [cardRows, setCardRows] = useState<number>(2);
    const [cardColumns, setCardColumns] = useState<number>(4);
    const [ref, node] = useCallbackRef();
    const { t } = useTranslation();
    const { api } = useApi();
    const { profile } = useProfile();
    const showDriverCode = profile.customer.settings.show_driver_code ?? false;

    const loadDriverScores = async (page: number, abortController?: AbortController): Promise<DriverScore[]> => {
        const res = await api.apiV0DriverScoresGet(
            { allDrivers: includeAllDrivers ? 1 : 0, page: page, cache: 1 },
            { signal: abortController?.signal },
        );
        const moreData = res.data.length > 0 ? await loadDriverScores(page + 1, abortController) : [];
        return [...res.data, ...moreData] as Array<DriverScore>;
    };

    const getDriverScores = (abortController?: AbortController) => {
        setIsLoading(true);
        loadDriverScores(1, abortController)
            .then((res) => {
                setDriverScores(res);
            })
            .catch(() => (abortController?.signal.aborted ? null : setIsError(true)))
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        document.title = `${DEFAULT_TITLE} | ${t(`navigator.${driversPageDefs.name}`)}`;
    }, []);

    useEffect(() => {
        const abortController = new AbortController();
        getDriverScores(abortController);
        return () => abortController.abort();
    }, [includeAllDrivers]);

    useEffect(() => {
        if (!node) return;
        const resizeObserver = new ResizeObserver(() => {
            if (node.offsetHeight) {
                const newRows = Math.floor(node.offsetHeight / (CARD_HEIGHT + CARD_MARGIN * 2));
                setCardRows(Math.max(1, newRows));
            }
            if (node.offsetWidth) {
                const newCols = Math.floor((node.offsetWidth - NAV_BUTTON_WIDTH * 2) / (CARD_WIDTH + CARD_MARGIN * 2));
                setCardColumns(Math.max(1, newCols));
            }
        });
        resizeObserver.observe(node);
        return () => resizeObserver.disconnect();
    }, [node]);

    const driversWithScore = driverScores
        .filter((driverScore) => driverScore.score >= 0)
        .sort((a, b) => {
            if (a.score === b.score) {
                return getDriverDisplayName(a.driver, showDriverCode).localeCompare(
                    getDriverDisplayName(b.driver, showDriverCode),
                );
            }
            return a.score - b.score;
        });
    const driversWithoutScore = driverScores
        .filter((driverScore) => driverScore.score === INVALID_SCORE)
        .sort((a, b) => {
            return getDriverDisplayName(a.driver, showDriverCode).localeCompare(
                getDriverDisplayName(b.driver, showDriverCode),
            );
        });
    const unwellDrivers = driversWithoutScore.filter((driverScore) => driverScore.unknown);
    const inactiveDrivers = driversWithoutScore.filter((driverScore) => driverScore.inactive);
    const sortedDrivers = [...driversWithScore, ...unwellDrivers, ...inactiveDrivers];
    const drivers = sortedDrivers.map((driverScore) => {
        const driver = {
            timestamp: driverScore.timestamp,
            eventIds: driverScore.event_ids || [],
            accidentEventIds: driverScore.accident_event_ids || [],
            driverName: getDriverDisplayName(driverScore.driver, showDriverCode),
            licensePlate: driverScore.license_plate,
            subFleet: driverScore.driver.sub_fleet,
            score: driverScore.score,
            behaviors: driverScore.event_types
                .split(',')
                .filter(Boolean)
                .map((eventType) => COMBINED_EVENT_TYPE_MAP[eventType] || eventType),
            mostRecentEvent: driverScore.most_recent_event,
            inactive: driverScore.inactive,
            unknown: driverScore.unknown,
            driver: driverScore.driver,
        };
        if (driver.accidentEventIds.length > 0) {
            driver.behaviors.unshift('content.safety.accident');
        }
        return driver;
    });

    const behaviorList = useMemo(() => {
        return new Set(
            drivers
                .map((driver) => driver.behaviors)
                .flat()
                .sort((a, b) => t(a).localeCompare(t(b))),
        );
    }, [drivers, t]);

    let data = drivers;
    if (searchText) {
        const normalizedNameText = searchText.toLocaleLowerCase();
        const normalizedLicensePlate = searchText.toLocaleUpperCase().replaceAll('-', '');
        data = data.filter((card) => {
            return (
                card.driverName?.toLocaleLowerCase().includes(normalizedNameText) ||
                card.licensePlate.toLocaleUpperCase().replaceAll('-', '').includes(normalizedLicensePlate)
            );
        });
    }
    if (subfleetValue && subfleetValue.length > 0) {
        data = data.filter((card) => subfleetValue.includes(card.subFleet?.toLocaleLowerCase()));
    }
    if (behaviorValue && behaviorValue.length > 0) {
        data = data.filter((card) => card.behaviors.some((behaviorItem) => behaviorValue.includes(behaviorItem)));
    }

    let content;
    if (isLoading) {
        content = (
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <CircularProgress size={40} />
            </Box>
        );
    } else if (isError) {
        content = (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                }}
            >
                <Typography variant="overline">{t('content.safety.error')}</Typography>
                <Link
                    id="try-again-link"
                    component="button"
                    variant="overline"
                    onClick={() => {
                        setIsError(false);
                        getDriverScores();
                    }}
                >
                    {t('content.safety.try_again')}
                </Link>
            </Box>
        );
    } else if (drivers.length > 0 && data.length === 0) {
        content = (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                }}
            >
                <Typography variant="overline">{t('content.safety.no_data')}</Typography>
                {!!searchText || subfleetValue.length > 0 || behaviorValue.length > 0 ? (
                    <Link
                        id="clear-filters-link"
                        component="button"
                        variant="overline"
                        onClick={() => {
                            setSubfleetValue([]);
                            setBehaviorValue([]);
                            setSearchText('');
                        }}
                    >
                        {t('content.safety.clear_filters')}
                    </Link>
                ) : null}
            </Box>
        );
    } else {
        content = <DriverGrid drivers={data} cardRows={cardRows} cardColumns={cardColumns} ref={ref} />;
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'start',
                width: '100%',
                height: '100%',
            }}
        >
            <DriversToolbarComponent
                searchText={searchText}
                setSearchText={setSearchText}
                subfleetValue={subfleetValue}
                setSubfleetValue={setSubfleetValue}
                behaviorValue={behaviorValue}
                setBehaviorValue={setBehaviorValue}
                behaviorOptions={[...behaviorList]}
                includeAllDrivers={includeAllDrivers}
                setIncludeAllDrivers={setIncludeAllDrivers}
            />
            {content}
        </Box>
    );
};

export default DriversComponent;
